import { Button } from 'components/ui/button';
import { buttonStyle } from 'components/buttons/buttonStyle';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import InfoTooltip from 'components/InfoTooltip';
import { useForm, useWatch } from 'react-hook-form';
import { MatterAssignmentCreateDto, MatterAssignmentForm } from 'types/assignments';
import { ClassifierData } from 'types/classifiers';
import { FeatureFlag } from 'types/features';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import ConfirmationModal from '../confirmation/ConfirmationModal';
import { ModalFooter } from '../ModalFooter';
import { ModalHeader } from '../ModalHeader';
import { useModal } from '../useModal';

interface Props {
    titleId?: string;
    onChange: (value: MatterAssignmentCreateDto) => void;
    isNonBillableProjectOrCase: boolean;
    onDeleteAssignment: () => Promise<void>;
    onClose: () => void;
    initialData?: MatterAssignmentForm;
    readOnly: boolean;
    classifiers: Record<string, ClassifierData>;
    type: 'edit' | 'create';
}

const defaultValues: MatterAssignmentForm = {
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    isNonBillable: false,
    preserveNonBillableHours: false,
    capLimit: 0,
    currencyTypeCode: '',
    matterId: 0,
};

const AssignmentModal = (props: Props) => {
    const {
        titleId = 'assignments.edit-assignment',
        initialData,
        onClose,
        onChange,
        isNonBillableProjectOrCase,
        onDeleteAssignment,
        readOnly,
        type,
    } = props;

    const { handleSubmit, control, setValue } = useForm<MatterAssignmentForm>({
        defaultValues: initialData ? initialData : defaultValues,
    });

    const { openModal, closeModal } = useModal();

    const onSave = async () => {
        await handleSubmit((data) => onChange(mapFormDataToDto(data)))();
    };

    const [isNonBillable] = useWatch({ control, name: ['isNonBillable'] });
    const hideDontNullifyOption = useIsFeatureEnabled(FeatureFlag.HIDE_DONT_NULLIFY_OPTION);

    const deleteButton = (
        <Button
            {...buttonStyle({ variant: 'outlined', color: 'error' }, 'text-sm font-normal')}
            key="delete"
            onClick={() => {
                if (initialData?.id !== undefined) {
                    openModal(
                        <ConfirmationModal
                            onCancel={closeModal}
                            onConfirm={() => {
                                closeModal();
                                onDeleteAssignment();
                            }}
                            content={<p className="text-body1">{t('assignments.delete-confirmation-text')}</p>}
                            cancelId="no"
                        />
                    );
                }
            }}
        >
            {t('delete')}
        </Button>
    );

    return (
        <>
            <ModalHeader title={readOnly ? t('assignments.view-assignment') : t(titleId)} onCancel={onClose} />
            <div className="h-full overflow-y-auto p-[38px] max-sm:p-6">
                <div className="flex flex-col gap-4">
                    <TextInput
                        name="name"
                        control={control}
                        label="name"
                        required
                        placeholder="assignments.name-placeholder"
                        readonly={readOnly}
                    />
                    <TextAreaInput
                        name="description"
                        control={control}
                        label="description"
                        placeholder="assignments.description-placeholder"
                        readonly={readOnly}
                    />
                    <div>
                        <div className="flex flex-row gap-4">
                            <div className="min-w-0 flex-1">
                                <DateInput
                                    name="startDate"
                                    fullWidth
                                    control={control}
                                    label={'assignments.opening-date'}
                                    required
                                    readonly={readOnly}
                                />
                            </div>
                            <div className="min-w-0 flex-1">
                                <DateInput
                                    name="endDate"
                                    fullWidth
                                    control={control}
                                    label={'assignments.closing-date'}
                                    readonly={readOnly}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row gap-4">
                        <CheckboxInput
                            name="isNonBillable"
                            control={control}
                            label={'assignments.non-billable'}
                            readonly={readOnly}
                            onChange={(isChecked: boolean) => {
                                if (!isChecked) {
                                    setValue('preserveNonBillableHours', false);
                                }
                            }}
                            disabled={isNonBillableProjectOrCase}
                        />
                        {isNonBillable && !hideDontNullifyOption ? (
                            <CheckboxInput
                                control={control}
                                name="preserveNonBillableHours"
                                label="timecards.preserve-non-billable-hours"
                                readonly={readOnly}
                                rightLabelComponent={
                                    <InfoTooltip text={'timecards.tooltips.preserve-non-billable-hours'} />
                                }
                            />
                        ) : null}
                    </div>
                    <div>
                        <div className="flex flex-row gap-4">
                            <div className="min-w-0 flex-1">
                                <DecimalInput
                                    name="capLimit"
                                    control={control}
                                    label={'assignments.cap-limit'}
                                    placeholder="assignments.cap-limit-placeholder"
                                    readonly={readOnly}
                                />
                            </div>
                            <div className="min-w-0 flex-1"></div>
                        </div>
                    </div>
                </div>
            </div>
            <ModalFooter
                {...(type === 'edit' ? { extraButton: deleteButton } : {})}
                onCancel={onClose}
                onSave={() => {
                    void onSave();
                }}
                readOnly={readOnly}
            />
        </>
    );
};

export default AssignmentModal;

const mapFormDataToDto = (formData: MatterAssignmentForm): MatterAssignmentCreateDto => ({
    name: formData.name,
    description: formData.description,
    startDate: formData?.startDate ? new Date(formData.startDate) : null,
    endDate: formData?.endDate ? new Date(formData.endDate) : null,
    isNonBillable: formData?.isNonBillable ?? false,
    preserveNonBillableHours: formData?.preserveNonBillableHours ?? false,
    capLimit: formData.capLimit,
    currencyTypeCode: 'eur',
    matterId: formData?.matterId,
});
